import {
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Code,
  Group,
  Loader,
  Select,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { useAuditActions, useAuditEntries } from '../api/hooks.js';
import type { AuditEntry } from '../api/hooks.js';
import { ErrorMessage } from '../components/ErrorMessage.js';
import { ApiError } from '../api/client.js';
import { useT } from '../i18n/useT.js';
import { formatDateTime } from '../format.js';

/**
 * Revisionsspor — who changed what, when, with which credential.
 *
 * Company-scoped, so it lives in the sidebar under Indstillinger rather than in
 * the header cluster: this is one company's history, and it changes completely
 * when the switcher moves.
 *
 * **Restricted to owner, system_admin, auditor and external_accountant.** A
 * bookkeeper or employee gets a 403, and this page says so in words rather than
 * showing an empty table — an audit trail that renders as "no entries" to
 * someone who simply may not read it is a lie about the books.
 *
 * Paging is by cursor and forward-only. The log is append-only and ordered
 * newest-first, so an offset pager would skip an entry whenever one was written
 * mid-read; a "previous" button would need a stack of cursors to be honest, and
 * nobody reading an audit trail has asked for one.
 */

/** The one row that is worth its own component: the payload folds. */
function EntryRow({ entry }: { readonly entry: AuditEntry }): ReactNode {
  const t = useT();
  const [open, setOpen] = useState(false);

  const who =
    entry.userName ?? (entry.userId === null ? null : entry.userId.slice(0, 8));

  return (
    <>
      <Table.Tr>
        <Table.Td style={{ whiteSpace: 'nowrap' }}>
          <Text size="sm">{formatDateTime(entry.createdAt)}</Text>
        </Table.Td>
        <Table.Td>
          <Text size="sm" ff="monospace">
            {entry.action}
          </Text>
        </Table.Td>
        <Table.Td>
          <Stack gap={2}>
            {/*
              A dash rather than blank when nobody did it. An inbound email is
              not somebody's doing, and an empty cell reads as missing data.
            */}
            <Text size="sm">{who ?? '—'}</Text>
            {entry.agentId !== null && (
              <Group gap={4} wrap="nowrap">
                <Badge size="xs" variant="light" color="grape" tt="none">
                  {entry.agentId}
                </Badge>
                {/*
                  The key by name, which is the whole point of migration 0020 --
                  "Claude Desktop", not `aaaaaaaa`. Without it, "revoke the key
                  on the laptop I lost" cannot be followed by "and show me what
                  it did".
                */}
                {entry.accessTokenName !== null && (
                  <Badge size="xs" variant="light" color="gray" tt="none">
                    {entry.accessTokenName}
                  </Badge>
                )}
              </Group>
            )}
          </Stack>
        </Table.Td>
        <Table.Td>
          {entry.payload === null ? (
            <Text size="xs" c="dimmed">
              —
            </Text>
          ) : (
            <Button
              size="compact-xs"
              variant="subtle"
              onClick={() => {
                setOpen((current) => !current);
              }}
            >
              {open ? t('audit.hideChange') : t('audit.showChange')}
            </Button>
          )}
        </Table.Td>
      </Table.Tr>

      {open && entry.payload !== null && (
        <Table.Tr>
          <Table.Td colSpan={4}>
            <Code block>{JSON.stringify(entry.payload, null, 2)}</Code>
            {entry.tableName !== null && (
              <Text size="xs" c="dimmed" mt={4}>
                {entry.tableName}
                {entry.recordId === null ? '' : ` · ${entry.recordId}`}
              </Text>
            )}
          </Table.Td>
        </Table.Tr>
      )}
    </>
  );
}

export function AuditPage(): ReactNode {
  const t = useT();
  const [action, setAction] = useState<string | null>(null);
  /*
    A stack of the cursors used to reach each page, so "back" is exact rather
    than recomputed. Reset whenever the filter changes -- a cursor from an
    unfiltered read means nothing in a filtered one.
  */
  const [cursors, setCursors] = useState<readonly string[]>([]);
  const cursor = cursors[cursors.length - 1];

  const entries = useAuditEntries({
    ...(action === null ? {} : { action }),
    ...(cursor === undefined ? {} : { cursor }),
  });
  const actions = useAuditActions();

  const forbidden =
    entries.error instanceof ApiError && entries.error.status === 403;

  return (
    <Stack gap="lg">
      <Title order={2}>{t('audit.title')}</Title>

      <Text c="dimmed">{t('audit.intro')}</Text>

      {forbidden ? (
        /*
          Said plainly. An empty table would be a lie -- there is history, this
          person may not read it -- and "Der opstod en fejl" would suggest
          something is broken.
        */
        <Alert color="gray" variant="light" title={t('audit.forbiddenTitle')}>
          {t('audit.forbidden')}
        </Alert>
      ) : (
        <>
          <Card withBorder padding="md">
            <Group align="flex-end" wrap="wrap">
              <Select
                label={t('audit.filterAction')}
                placeholder={t('audit.allActions')}
                clearable
                searchable
                maw={320}
                value={action}
                data={actions.data?.items ?? []}
                disabled={actions.isPending}
                onChange={(value) => {
                  setAction(value);
                  setCursors([]);
                }}
              />
            </Group>
          </Card>

          {entries.isPending && (
            <Center p="xl">
              <Loader />
            </Center>
          )}

          {entries.isError && !forbidden && (
            <ErrorMessage error={entries.error} title={t('audit.loadFailed')} />
          )}

          {entries.data !== undefined && entries.data.items.length === 0 && (
            <Text c="dimmed">{t('audit.empty')}</Text>
          )}

          {entries.data !== undefined && entries.data.items.length > 0 && (
            <Table.ScrollContainer minWidth={720}>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ width: 150 }}>{t('audit.col.when')}</Table.Th>
                    <Table.Th style={{ width: 240 }}>{t('audit.col.action')}</Table.Th>
                    <Table.Th>{t('audit.col.who')}</Table.Th>
                    <Table.Th style={{ width: 130 }}>{t('audit.col.change')}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {entries.data.items.map((entry) => (
                    <EntryRow key={entry.id} entry={entry} />
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}

          <Group>
            <Button
              variant="default"
              disabled={cursors.length === 0}
              onClick={() => {
                setCursors((current) => current.slice(0, -1));
              }}
            >
              {t('audit.newer')}
            </Button>
            <Button
              variant="default"
              disabled={entries.data?.nextCursor == null}
              onClick={() => {
                const next = entries.data?.nextCursor;
                if (next != null) {
                  setCursors((current) => [...current, next]);
                }
              }}
            >
              {t('audit.older')}
            </Button>
          </Group>

          <Text size="xs" c="dimmed">
            {t('audit.immutable')}
          </Text>
        </>
      )}
    </Stack>
  );
}
